import { ChevronDownIcon } from '@chakra-ui/icons'
import { Box, Button, Collapse, Flex, Stack, Text } from '@chakra-ui/react'
import { FAQItem, FAQList, useAppModals } from '@hb/react'
import {
  Info,
  InfoBox,
  InfoHeader,
  InfoText,
  InlineLink,
  MyAssessmentShortcut,
  PageContainer,
  PageHeader,
  ScheduleCallModal,
  siteVersions,
  ViewHeaderImage,
} from '@hb/react-web'
import { useState } from 'react'
import { useImagesLoaded } from '../../hooks/useImagesLoaded'
import { benefitsAndCoverageList } from './benefitsAndCoverage'
import { claimsFaqList } from './claims'
import faqImage from './faqImage.png'
import { insuranceTermsFaq } from './insuranceTerms'
import { PatientsPageLink } from './PatientsPageLink'

export const FAQItemView = ({
  body,
  title,
  subtitle,
  size = 'md',
}: FAQItem & { size?: 'sm' | 'md' | 'lg' }) => {
  const [isIn, setIsIn] = useState(false)
  return (
    <Flex width="100%" direction="column">
      <Button
        onClick={() => {
          setIsIn(i => !i)
        }}
        borderTopRadius={18}
        borderBottomRadius={isIn ? 0 : 18}
        fontWeight={400}
        fontSize={size === 'sm' ? 'large' : 'xl'}
        height="auto"
        py={size === 'sm' ? 3 : 4}
        boxShadow="1px 1px 4px rgba(0,0,0,0.4)"
        px={size === 'sm' ? 4 : 6}
        whiteSpace="pre-wrap"
        textAlign="left"
        color="white"
        transition="all 300ms"
        background="green.500"
        _hover={{ background: '#65CFBF' }}
        _dark={{ background: 'green.600', _hover: { background: 'green.700' } }}
        fontFamily="hero-new">
        <Box>
          <Text>{title}</Text>
          {subtitle ? (
            <Text color="gray.50" fontFamily="Open Sans" mt={1} fontSize="md">
              {subtitle}
            </Text>
          ) : null}
        </Box>
        <ChevronDownIcon transform={`rotate(${isIn ? 180 : 0}deg)`} ml="auto" color="white" />
      </Button>
      <Collapse in={isIn}>
        <Info
          fontSize={size === 'sm' ? 'xs' : 'sm'}
          // textAlign={['left', 'left', 'justify']}
          py={4}
          px={2}
          boxShadow="inset 0 0 6px rgba(0,0,0,0.4)"
          bg="whiteAlpha.500"
          _dark={{ bg: 'blackAlpha.500' }}
          width="100%"
          borderBottomRadius={18}>
          {body}
        </Info>
      </Collapse>
    </Flex>
  )
}

export const FAQListView = ({
  name,
  items,
  description,
  size = 'md',
}: FAQList & { size?: 'sm' | 'md' | 'lg' }) => (
  <Box>
    <InfoHeader
      fontSize={size === 'sm' ? 'lg' : '28px'}
      opacity={0.9}
      style={{
        textAlign: size === 'sm' ? 'left' : 'center',
      }}>
      {name}
      {/* {name.toUpperCase()} */}
    </InfoHeader>
    {description ? <Box mt={4}>{description}</Box> : null}
    <Stack width="100%" mt={6} mb={size === 'sm' ? 6 : 12} spacing={size === 'sm' ? 3 : 5}>
      {items.map((item, i) => (
        <FAQItemView size={size} key={i} {...item} />
      ))}
    </Stack>
  </Box>
)

const ResourcesView = () => {
  const { loaded, onLoad } = useImagesLoaded(1)
  const { scheduleCall } = useAppModals()
  return (
    <>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg={siteVersions.prod.background}
        _dark={{ bg: siteVersions.prod.darkBackground }}
      />
      <PageContainer pb={16} transition="opacity 300ms" opacity={loaded ? 1 : 0}>
        <PageHeader>{'FAQ & Resources'}</PageHeader>
        <ViewHeaderImage onLoad={onLoad} src={faqImage} />
        <InfoBox spacing={2}>
          <InfoText>
            See below for FAQ and a glossary of terms to help translate insurance language into
            plain English! If you need to contact us about assessing coverage from your insurance
            plan, you can do so by{' '}
            <InlineLink
              onClick={() => {
                scheduleCall.open('faq')
              }}>
              clicking here
            </InlineLink>{' '}
            to schedule a call.
          </InfoText>
          <InfoText mb={2}>
            Otherwise, please see our <PatientsPageLink /> to get started on a free assessment.
          </InfoText>
        </InfoBox>
        <Stack maxW="90%" mt={14} spacing={6}>
          <FAQListView {...benefitsAndCoverageList} />
          <FAQListView {...claimsFaqList} />
          <FAQListView {...insuranceTermsFaq} />
        </Stack>
        <ScheduleCallModal id="faq" />
        <MyAssessmentShortcut />
      </PageContainer>
    </>
  )
}

export default ResourcesView
